import {
  AdgroupFormData,
  CampaignFormData,
  InterestOption,
  InterestScene,
  InterestSearchResult,
  InterestKeywordsInfoMap,
} from '@tt4b/ttam-enums';
import { Ref, computed, ref, watch } from '@vue/composition-api';
import {
  useGetInterestsSearchResultApi,
  useGetRecommendedInterestKeywordsApi,
  useRequest,
  useAllowlist,
  useInterstKeywordsInfoApi,
  useStore,
  NetStateEnum,
  StateEnum,
} from '@tt4b/ttam-hooks';
import { getIdLabelMap, setParent, walkTreeBFS } from '@tt4b/ttam-utils';
import { cloneDeep, debounce, reverse, uniqBy } from 'lodash-es';
import { i18n } from './../../../../i18n';
import { trackEvent } from './../../../../utils/index';
import TreeStore from 'byted-vui/packages/tree/src/model/tree-store';
import { recommendInterestKeywordsList } from './../../recommend-interest-behavior/use-recomment-data';
import { createSharedComposable } from '@vueuse/core';

const interestList = ref<InterestOption[]>([]);
const interestKeywordsInfoMap = ref<InterestKeywordsInfoMap>({});
const keywordSearchResult = ref<InterestSearchResult[]>([]);
const keyword = ref<string>('');
const searchResultHistory = ref<InterestSearchResult[]>([]);

const logSearchResultHistory = results =>
  results.forEach(item => {
    if (item.scene === InterestScene.KEYWORD && !searchResultHistory.value.find(o => item.id === o.id)) {
      searchResultHistory.value.push(item);
    }
  });

const interestCategoriesInfoMap = computed(() => {
  const map = {};
  walkTreeBFS({
    root: { children: cloneDeep(interestList.value) },
    handler: node => {
      map[node.id] = node;
      node.keyword_id = node.id;
      node.keyword = node.i18n_value;
    },
  });

  return map;
});

const interestOptions = computed(() => interestList.value.map(({ id }) => interestCategoriesInfoMap.value[id]));

const interestCategories = computed(() => {
  const categories = cloneDeep(interestOptions.value);
  categories.forEach(item => Object.assign(item, { hide: true, visible: false }));

  return categories;
});
const interestCategoriesMap = computed(() => {
  const map = {};
  walkTreeBFS({ root: { children: interestCategories.value }, handler: node => (map[node.id] = node) });

  return map;
});

const interestMap = computed(() => getIdLabelMap(interestOptions.value, 'i18n_value'));

/** 对于子级兴趣类目词返回其顶级祖先的 label，对于顶级兴趣类目词返回"Interest"的 i18n */
function findAncestorLabel(currentNode, depth = 0) {
  if (currentNode.parent?.value) {
    return findAncestorLabel(currentNode.parent, depth + 1);
  }

  return depth ? currentNode.value : i18n.t('module_adgroup_behavior_interest_label');
}

export function handleLoadedInterests(result, adgroupData: Ref<AdgroupFormData>, keepLang = true) {
  const keywordsMap = { ...interestKeywordsInfoMap.value };
  result?.forEach(item => {
    item.i18n_value = item.keyword;
    if (item.scene === InterestScene.KEYWORD) {
      item.id = `${item.keyword_id}-keyword`;
      item.type = i18n.t('adgroup_interest_keywords_additional_interests');
      if (
        keywordsMap[item.keyword_id] &&
        adgroupData.value?.interestKeywordsI18n?.includes?.(item.keyword_id) &&
        keepLang
      ) {
        // 已选中的项应该保留选中时的语言
        Object.assign(item, keywordsMap[item.keyword_id]);
      } else {
        keywordsMap[item.keyword_id] = item;
      }
    } else {
      Object.assign(item, {
        ...interestCategoriesMap.value[item.keyword_id],
        hide: false,
        visible: true,
        type: findAncestorLabel(item, 0),
      });
    }
  });

  interestKeywordsInfoMap.value = keywordsMap;
  const { state } = useStore();
  state[StateEnum.targetInfo].value.interestKeywordsInfoMap = interestKeywordsInfoMap.value;

  return result;
}

/** @description 基于兴趣类目词的树状关系，合并子节点的选择减少冗余 id */
export function useInterestCategoryChildrenMerger(props: { interestOptions: Ref<unknown[]> }) {
  const treeStore = new TreeStore({
    key: 'id',
    data: props?.interestOptions?.value,
    lazy: false,
    props: { label: 'i18n_value' },
    filterType: 'tree',
    checkStrictly: false,
    checkDescendants: false,
    defaultCheckedKeys: [],
    defaultExpandedKeys: [],
    autoExpandParent: true,
    defaultExpandAll: false,
    filterNodeMethod: () => true,
  });

  watch(props?.interestOptions, value => treeStore.setData(value));

  function mergeInterestCategoryChildren(checkedKeys) {
    treeStore.setCheckedKeys(checkedKeys);

    return treeStore.getCheckedKeys(false, true);
  }
  function _mergeInterestCategoryChildren(ids?: number[]) {
    if (Array.isArray(ids)) {
      return mergeInterestCategoryChildren(ids);
    } else {
      return ids;
    }
  }
  return {
    mergeInterestCategoryChildren,
    mergeInterestCategoryChildrenIds: _mergeInterestCategoryChildren,
  };
}

export function useCommonInterestsInterestOptionsDataSource(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { getStore } = useStore();
  const { isLoading, error, isEmpty, run, data: _interestList } = getStore(NetStateEnum.interestList);

  watch(error, newVal => {
    if (newVal) {
      interestList.value = [];
    }
  });

  watch(_interestList, newVal => {
    if (newVal) {
      setParent({ children: newVal });
      interestList.value = cloneDeep(newVal) as InterestOption[];
    }
  });

  return {
    data: interestList,
    interestMap,
    isLoading,
    error,
    isEmpty,
    run,
  };
}

const isKeywordsInfoLoaded = ref(false);
export function useCommonInterestsInterestKeywordsInfoDataSource(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;

  const params = computed(() => ({
    keywordInfos: adgroupData.value.interestKeywordsI18n.map((keyword_id, idx) => ({
      keyword_id,
      lang: adgroupData.value.interestKeywordsLangI18n[idx],
    })),
    industryTypes: campaignData.value.industryTypes ?? [],
  }));

  const { isLoading, error, isEmpty, run } = useRequest(useInterstKeywordsInfoApi, {} as any, {
    manual: true,
  });

  watch(error, newVal => {
    if (newVal) {
      isKeywordsInfoLoaded.value = false;
    }
  });
  return {
    data: interestList,
    isLoading,
    error,
    isEmpty,
    run: () => {
      isKeywordsInfoLoaded.value = false;
      if (!adgroupData?.value?.interestKeywordsI18n?.length) {
        isKeywordsInfoLoaded.value = true;
        return;
      }
      run(params.value).then(data => {
        handleLoadedInterests(data, adgroupData);
        logSearchResultHistory(data);
        isKeywordsInfoLoaded.value = true;
      });
    },
  };
}

export function useCommonInterestsInterestSearchResultDataSource(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;
  const outerLoading = ref(false);

  const { isEmpty, isLoading, error, run, cancel } = useRequest(useGetInterestsSearchResultApi, {} as any, {
    manual: true,
  });

  watch(error, newVal => {
    if (newVal) {
      logSearchResultHistory(keywordSearchResult.value);
      keywordSearchResult.value = [];
      outerLoading.value = false;
    }
  });

  const adgroupId = computed(() => adgroupData.value?.adId || '');

  const searchTea3S = debounce(function searchTeaHandle() {
    trackEvent('create_campaign_targeting_interest_searchresult_impression', {
      keyword: keyword.value,
      number: keywordSearchResult.value.length,
      adgroup_id: adgroupId.value,
    });
  }, 3000);

  watch(keyword, (value, oldValue) => {
    searchTea3S();
    // 点击X埋点
    if (value === '' && oldValue.length > 0) {
      if (keywordSearchResult.value.length === 0 && oldValue.length >= 3) {
        trackEvent('create_campaign_targeting_interest_searchmiss_empty', {
          keyword: oldValue,
          adgroup_id: adgroupId.value,
        });
      }
      trackEvent('create_campaign_targeting_interest_searchbox_cross_click', {
        number: keywordSearchResult.value.length,
        adgroup_id: adgroupId.value,
      });
    }
  });

  let timer;

  return {
    data: keywordSearchResult,
    keyword,
    error,
    isLoading: computed(() => isLoading.value || outerLoading.value),
    isEmpty,
    run: word => {
      cancel?.();
      keyword.value = word;
      outerLoading.value = true;
      if (timer) {
        window.clearTimeout(timer);
      }

      timer = window.setTimeout(() => {
        run({
          keyword: word,
          scenes: [InterestScene.CATEGORY, InterestScene.KEYWORD],
          industryTypes: campaignData.value.industryTypes,
        }).then(data => {
          if (word !== keyword.value) {
            return;
          }
          handleLoadedInterests(data, adgroupData);
          logSearchResultHistory(keywordSearchResult.value);
          keywordSearchResult.value = data;

          outerLoading.value = false;
        });
      }, 1000);
    },
  };
}

export const useIsInterestKeywordsAvailable = createSharedComposable(function useIsInterestKeywordsAvailable() {
  return computed(() => isKeywordsInfoLoaded.value);
});

export function useCommonInterestsCategoryPropsOptions() {
  const isInterestKeywordsAvailable = useIsInterestKeywordsAvailable();

  const searchResultTree = computed<InterestSearchResult[]>(() => {
    if (!keyword.value) {
      return [
        ...interestOptions.value,
        ...recommendInterestKeywordsList.value.map(item => ({ ...item, hide: true, visible: false })),
        ...searchResultHistory.value.map(item => ({ ...item, hide: true, visible: false })),
      ];
    }

    return reverse(
      uniqBy(
        reverse([
          ...interestCategories.value,
          ...searchResultHistory.value.map(item => ({ ...item, hide: true, visible: false })),
          ...recommendInterestKeywordsList.value.map(item => ({ ...item, hide: true, visible: false })),
          ...keywordSearchResult.value,
        ]),
        'id',
      ),
    );
  });

  return {
    options: computed(() => (isInterestKeywordsAvailable.value ? searchResultTree.value : interestOptions.value)),
  };
}

const nodeShouldHide = node => Boolean(node.hide || (node.parent && nodeShouldHide(node.parent)));

export function useCommonInterestsCategoryPropsFilterMethod(props: { interestOptions: Ref<any[]> }) {
  const isInterestKeywordsAvailable = useIsInterestKeywordsAvailable();
  function interestKeywordsFilter() {
    return (_keywords, node) => {
      /** 针对关键字搜索结果中的正常兴趣定向单独判断 */
      if (props?.interestOptions?.value?.find?.(item => item.id === node.id) && !node.hide && node.type) {
        return true;
      }

      return !nodeShouldHide(node);
    };
  }
  return {
    filterMethod: computed(() => (isInterestKeywordsAvailable.value ? interestKeywordsFilter : () => () => true)),
  };
}

export function useCommonInterestsRecommendKeywordsDataSource(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;
  const recommendationSearchKeyword = computed(() =>
    [
      ...[adgroupData?.value?.adTagV2?.map?.(id => interestCategoriesMap.value[id]?.i18n_value)],
      ...adgroupData?.value?.interestKeywordsI18n?.map?.(id => interestKeywordsInfoMap.value[id]?.i18n_value),
    ]
      .filter(Boolean)
      .join(','),
  );

  const recommendationParams = computed(() => ({
    selectedInterests: recommendationSearchKeyword.value,
    industryTypes: campaignData.value.industryTypes,
  }));

  const recommendedInterestKeywords = ref<InterestSearchResult[]>([]);

  const { isEmpty, isLoading, error, run } = useRequest(useGetRecommendedInterestKeywordsApi, {} as any, {
    manual: true,
  });

  const wrapFn = debounce(
    params => {
      run({
        ...params,
        industryTypes: campaignData.value.industryTypes,
      }).then(result => {
        if (params.selectedInterests !== recommendationSearchKeyword.value) {
          return;
        }
        const _result = result.filter(item => !adgroupData.value.interestKeywordsI18n.includes(item?.keyword_id));
        handleLoadedInterests(_result, adgroupData);
        logSearchResultHistory(_result);
        recommendedInterestKeywords.value = _result;
      });
    },
    300,
    { leading: false, trailing: true, maxWait: 500 },
  );

  watch(recommendationSearchKeyword, newVal => {
    if (!newVal) {
      recommendedInterestKeywords.value = [];
      return;
    }
    wrapFn({
      selectedInterests: newVal,
    });
  });

  return {
    data: recommendedInterestKeywords,
    error,
    isLoading,
    isEmpty,
  };
}
